import type { RouteRecordRaw } from 'vue-router';
import type { IconName } from '@/icons/catalog';
import { MANUAL_SHEET_CREATION_ENABLED, PERMISSION, type PermissionCode } from '@dsv/shared';

declare module 'vue-router' {
  interface RouteMeta {
    /** 是否需要登录，默认 true；公开页面显式写 false */
    requiresAuth?: boolean;
    /** 访问所需权限点（需全部满足）。管理员自动放行。 */
    permissions?: PermissionCode[];
    /** 访问所需权限点（满足任一即可）；与 permissions 共同使用时仍须满足 permissions。 */
    anyPermissions?: PermissionCode[];
    /**
     * 侧边栏菜单显示所需权限点（需全部满足）。与 permissions 分开：
     * 页面本身能看（直链可进），但日常用不到的角色不在菜单里挂噪音。
     */
    menuPermissions?: PermissionCode[];
    menuAnyPermissions?: PermissionCode[];
    /** 侧边栏/页签标题的 i18n key */
    titleKey?: string;
    /** 侧边栏图标名，对应 AppIcon 的 name */
    icon?: IconName;
    /** 是否在侧边栏菜单中显示 */
    inMenu?: boolean;
    /**
     * 仅管理员可见（开发阶段的辅助页）。菜单过滤与路由守卫都认它 ——
     * 普通用户直敲 URL 也进不去。上线前这些页面随清单撤掉。
     */
    adminOnly?: boolean;
    businessOnly?: boolean;
    factoryPortal?: boolean;
    plannerWorkspace?: boolean;
  }
}

export const routes: RouteRecordRaw[] = [
  {path:'/factory',name:'factory-portal',component:()=>import('@/views/factory/FactoryPortalView.vue'),meta:{requiresAuth:false,factoryPortal:true,titleKey:'factoryCollab.portalTitle'}},
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false, titleKey: 'auth.loginTitle' },
  },

  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {path:'workbench/manage',name:'style-management',component:()=>import('@/views/style-library/StyleManagementView.vue'),meta:{titleKey:'styleIntake.manage',permissions:[PERMISSION.SHEET_ASSIGN]}},
      {path:'workbench',name:'style-workbench',component:()=>import('@/views/style-library/StyleWorkbenchView.vue'),meta:{titleKey:'styleIntake.workbench',icon:'workspace',inMenu:true,permissions:[PERMISSION.SHEET_VIEW]}},
      {
        // 款式库（2026-10-23）：全部款式按季节/品类/状态用文件夹卡片浏览
        path: 'style-library',
        name: 'style-library',
        redirect: to => ({path:'/workbench',query:{...to.query,view:'all'}}),
        meta: {
          titleKey: 'styleLibrary.title',
          icon: 'styles',
          inMenu: false,
          permissions: [PERMISSION.SHEET_VIEW],
        },
      },
      { path:'designer-drafts', name:'designer-drafts', component:()=>import('@/views/designer-draft/DesignerDraftsView.vue'), meta:{titleKey:'designerDraft.title',icon:'styles',inMenu:false,businessOnly:true,permissions:[PERMISSION.SHEET_CREATE]} },
      { path:'designer-drafts/:id', name:'designer-draft', component:()=>import('@/views/designer-draft/DesignerDraftView.vue'), meta:{titleKey:'designerDraft.title',businessOnly:true,permissions:[PERMISSION.SHEET_CREATE]} },
      {path:'factory-batches',name:'factory-batches',component:()=>import('@/views/factory/FactoryBatchView.vue'),meta:{titleKey:'factoryCollab.batch',icon:'send',inMenu:true,permissions:[PERMISSION.FACTORY_SHARE_MANAGE]}},
      { path: 'styles', name: 'planner-styles', component: () => import('@/views/planner-style/PlannerStylesView.vue'),
        meta: { plannerWorkspace: true, titleKey: 'plannerStyle.title', icon: 'styles', inMenu: MANUAL_SHEET_CREATION_ENABLED, businessOnly: true, permissions: [PERMISSION.SHEET_ASSIGN] } },
      { path: 'styles/batch', name: 'planner-batch-new', beforeEnter: () => MANUAL_SHEET_CREATION_ENABLED ? true : { name: 'planner-styles' }, component: () => import('@/views/planner-style/PlannerBatchView.vue'), meta: { plannerWorkspace: true, titleKey: 'plannerBatch.title', businessOnly: true, permissions: [PERMISSION.SHEET_ASSIGN] } },
      { path: 'styles/batches/:id', name: 'planner-batch', component: () => import('@/views/planner-style/PlannerBatchView.vue'), meta: { plannerWorkspace: true, titleKey: 'plannerBatch.title', businessOnly: true, permissions: [PERMISSION.SHEET_ASSIGN] } },
      { path: 'styles/drafts/:id', name: 'planner-style-draft', component: () => import('@/views/planner-style/PlannerStyleView.vue'),
        meta: { plannerWorkspace: true, titleKey: 'plannerStyle.title', businessOnly: true, permissions: [PERMISSION.SHEET_ASSIGN] } },
      { path: 'styles/sheets/:id', name: 'planner-style-sheet', component: () => import('@/views/planner-style/PlannerStyleView.vue'),
        meta: { plannerWorkspace: true, titleKey: 'plannerStyle.title', businessOnly: true, permissions: [PERMISSION.SHEET_ASSIGN] } },
      {
        // SAP owns intake; preserve old bookmarks without exposing the retired OrderEase page.
        path: 'order-intakes', name: 'order-intakes', redirect: '/workbench',
        meta: { titleKey: 'intake.title', inMenu: false },
      },
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { titleKey: 'nav.dashboard', icon: 'workspace', inMenu: true },
      },

      {
        // 委托书列表已并入工作台的商品看板（2026-10-02 合并，用户确认）。
        // 保留路由做兼容重定向：旧链接 /design-sheets?status=PENDING → /?status=PENDING
        path: 'design-sheets',
        name: 'sheet-list',
        redirect: (to) => ({ name: 'dashboard', query: { ...to.query } }),
      },
      {
        // 自建辅料库（P9 D4）。看委托书的人都能查（设计师录 BOM 要用），维护按钮按 submaterial:manage 显示。
        path: 'sub-materials',
        name: 'sub-material-library',
        component: () => import('@/views/sub-material/SubMaterialLibraryView.vue'),
        meta: {
          titleKey: 'nav.subMaterials',
          icon: 'trims',
          inMenu: true,
          permissions: [PERMISSION.SUBMATERIAL_VIEW],
        },
      },
      { path: 'fabric-management', name: 'fabric-workspace', component: () => import('@/views/fabric/FabricWorkspaceView.vue'), meta: { titleKey: 'fabricWorkspace.title', icon: 'fabric', inMenu: true, permissions: [PERMISSION.FABRIC_LIBRARY_VIEW] } },
      { path: 'fabric-management/:pathMatch(.*)*', name: 'fabric-workspace-detail', component: () => import('@/views/fabric/FabricWorkspaceView.vue'), meta: { titleKey: 'fabricWorkspace.title', permissions: [PERMISSION.FABRIC_LIBRARY_VIEW] } },
      {
        path: 'fabric-stock', name: 'fabric-stock',
        redirect: to => ({ path: '/fabric-management/inventory', query: to.query }),
        meta: { titleKey: 'fabricStockInit.title', inMenu: false, permissions: [PERMISSION.FABRIC_LIBRARY_VIEW] },
      },
      { path: 'fabrics', name: 'fabric-library', component: () => import('@/views/fabric/FabricLibraryView.vue'),
        meta: { titleKey: 'fabric.title', icon: 'fabric', inMenu: false, permissions: [PERMISSION.FABRIC_LIBRARY_VIEW] } },
      {
        // 供应商管理（2026-10-22）：面辅料共用同一张供应商主档，
        // 从面料工作台与辅料页抽出来作为主数据统一维护，菜单挂在「基础数据」。
        path: 'master/suppliers',
        name: 'master-suppliers',
        component: () => import('@/views/master-data/SupplierMasterView.vue'),
        meta: {
          titleKey: 'nav.supplierMaster',
          icon: 'suppliers',
          inMenu: true,
          anyPermissions: [PERMISSION.SUBMATERIAL_VIEW, PERMISSION.FABRIC_LIBRARY_VIEW, PERMISSION.FACTORY_SHARE_MANAGE],
          menuAnyPermissions: [PERMISSION.SUBMATERIAL_MANAGE, PERMISSION.FABRIC_LIBRARY_MANAGE, PERMISSION.FACTORY_SHARE_MANAGE],
        },
      },
      {
        // 基础字典（2026-10-23 合并）：商品颜色 / 材料颜色 / 尺码-类别映射，
        // 一页三个页签，按 master:dict:manage 维护（默认系统管理员）。
        path: 'master/dictionary',
        name: 'master-dictionary',
        component: () => import('@/views/master-data/MasterDictsView.vue'),
        props: { scope: 'all' },
        meta: {
          titleKey: 'nav.menu.masterDict',
          icon: 'dictionary',
          inMenu: true,
          permissions: [PERMISSION.SHEET_VIEW],
          menuPermissions: [PERMISSION.MASTER_DICT_MANAGE],
        },
      },
      {
        path: 'master/used-parts',
        name: 'master-used-parts',
        component: () => import('@/views/master-data/UsedPartsView.vue'),
        meta: { titleKey: 'usedParts.title', icon: 'dictionary', inMenu: true, permissions: [PERMISSION.MASTER_DICT_MANAGE] },
      },
      {
        path: 'master/pattern-parts', name: 'master-pattern-parts',
        component: () => import('@/views/master-data/PatternPartsView.vue'),
        meta: { titleKey: 'patternParts.title', icon: 'measurement-point', inMenu: true,
          permissions: [PERMISSION.SHEET_VIEW], menuPermissions: [PERMISSION.MASTER_DICT_MANAGE] },
      },
      {
        // 旧地址保留，避免书签失效（颜色页、尺码页曾各自独立过）
        path: 'master/colors',
        name: 'master-colors',
        redirect: '/master/dictionary',
      },
      {
        path: 'master/sizes',
        name: 'master-sizes',
        redirect: '/master/dictionary',
      },
      {
        // 面料编码规则（2026-10-23）：类别字母 + 关键因子 KF1，面料库下拉以此为准。
        // 写操作后端限 ADMIN，菜单也只挂管理员（adminOnly）。
        path: 'master/fabric-code-rules',
        name: 'master-fabric-code-rules',
        component: () => import('@/views/master-data/FabricCodeRuleView.vue'),
        meta: {
          titleKey: 'fabricCodeRule.title',
          icon: 'admin',
          inMenu: true,
          adminOnly: true,
          permissions: [PERMISSION.MATERIAL_VIEW],
        },
      },
      {
        // 旧地址保留，避免书签失效
        path: 'master/dicts',
        name: 'master-dicts',
        redirect: '/master/dictionary',
      },
      {
        // 旧系统（EADS）历史委托书，design_sheet_legacy 快照的只读查询（09-29 迁移）。
        // 能看委托书的人都能看；品牌隔离在接口层生效。
        path: 'legacy/sheets',
        name: 'legacy-sheet-list',
        component: () => import('@/views/legacy/LegacySheetListView.vue'),
        meta: {
          titleKey: 'nav.legacySheets',
          icon: 'archive',
          inMenu: true,
          permissions: [PERMISSION.SHEET_VIEW],
        },
      },
      {
        path: 'legacy/sheets/:modelCd/:version',
        name: 'legacy-sheet-detail',
        component: () => import('@/views/legacy/LegacySheetDetailView.vue'),
        meta: {
          titleKey: 'nav.legacySheets',
          permissions: [PERMISSION.SHEET_VIEW],
        },
      },
      {
        // 汇率与成本参数（P9 D1）。所有能看委托书的人都能看（价格与成本对所有人可见），
        // 维护按钮在页面里按 cost:config:manage 显示。
        // 菜单只挂给能维护的人 —— 只有维护者需要天天进这页（C 类二-2）。
        path: 'cost/config',
        name: 'cost-config',
        component: () => import('@/views/cost/CostConfigView.vue'),
        meta: {
          titleKey: 'nav.costConfig',
          icon: 'cost',
          inMenu: true,
          permissions: [PERMISSION.SHEET_VIEW],
          menuPermissions: [PERMISSION.COST_CONFIG_MANAGE],
        },
      },
      {
        // 审计日志（1.0 改善 #3）：跨对象修改留痕查询。「每一步有迹可循」的另一半
        path: 'audit-logs',
        name: 'audit-logs',
        component: () => import('@/views/audit/AuditLogView.vue'),
        meta: {
          titleKey: 'nav.auditLog',
          icon: 'history',
          inMenu: true,
          adminOnly: true,
          permissions: [PERMISSION.ADMIN_AUDIT_VIEW],
        },
      },
      {
        // 加单看板（ARS 合并）
        path: 'reorder-board',
        name: 'reorder-board',
        component: () => import('@/views/reorder/ReorderBoardView.vue'),
        meta: { titleKey: 'reorder.board.title', icon: 'reorder', inMenu: true, permissions: [PERMISSION.REORDER_VIEW] },
      },
      {
        // 加单生产确认（ARS 合并）
        path: 'reorder-production',
        name: 'reorder-production',
        component: () => import('@/views/reorder/ProductionConfirmView.vue'),
        meta: { titleKey: 'reorder.prod.title', icon: 'confirm', inMenu: true, permissions: [PERMISSION.REORDER_PRODUCTION] },
      },
      {
        // 加单（ARS 合并）：历史画面
        path: 'reorder-history',
        name: 'reorder-history',
        component: () => import('@/views/reorder/ReorderHistoryView.vue'),
        meta: { titleKey: 'reorder.history.title', icon: 'history', inMenu: true, permissions: [PERMISSION.REORDER_VIEW] },
      },
      {
        // 关于 Design Sheet:软件版本(构建期注入)、版本规则。软件版本与委托书版本/QC 轮次是独立概念
        path: 'about',
        name: 'about',
        component: () => import('@/views/about/AboutView.vue'),
        meta: { titleKey: 'nav.about', icon: 'info', inMenu: false },
      },
      {
        // SAP 传送状态（1.0 D8）：sap_transfer_log 列表 + 失败人工重发。
        // 执行传送的入口在委托书详情页顶栏；这一页只看记录。
        path: 'sap/transfers',
        name: 'sap-transfers',
        component: () => import('@/views/sap-transfer/SapTransfersView.vue'),
        meta: {
          titleKey: 'nav.sapTransfers',
          icon: 'sync',
          inMenu: true,
          permissions: [PERMISSION.SAP_TRANSFER_VIEW],
        },
      },
      {
        // 详情页承载第 5~13 步：同一个委托书聚合根的 9 个视图，用 ?step= 切换。
        // 不挂菜单 —— 入口是列表页。
        path: 'design-sheets/:id',
        name: 'sheet-detail',
        component: () => import('@/views/design-sheet/SheetDetailView.vue'),
        meta: { titleKey: 'nav.sheetDetail', permissions: [PERMISSION.SHEET_VIEW] },
      },

      {
        // 临时页：看当前环境能不能连通 EADS↔SAP 的接口。
        // 接口全部接好后这一页可以撤掉，或并进系统管理。
        path: 'integrations',
        name: 'integrations',
        component: () => import('@/views/integration/IntegrationStatusView.vue'),
        meta: {
          titleKey: 'nav.integrations',
          icon: 'connection',
          inMenu: true,
          adminOnly: true,
          permissions: [PERMISSION.INTEGRATION_VIEW],
        },
      },

      {
        // 权限说明：所有登录用户都能看 —— 开会讨论权限时人人打得开。
        // 页面内容全部由代码里的定义渲染，不是手写文档，所以不会过期。
        path: 'permissions',
        name: 'permissions',
        component: () => import('@/views/meta/PermissionsView.vue'),
        meta: { titleKey: 'nav.permissions', icon: 'permission', inMenu: true, adminOnly: true },
      },

      {
        path: 'admin/brands',
        name: 'brand-management',
        component: () => import('@/views/admin/BrandManagementView.vue'),
        meta: { titleKey: 'brandAdmin.title', icon: 'brand', inMenu: true, permissions: [PERMISSION.ADMIN_BRAND_VIEW] },
      },
      {
        path: 'admin/tasks',
        redirect: to => ({ path: '/tasks/assignments', query: { ...to.query } }),
      },
      {
        path: 'tasks/assignments',
        name: 'task-assignment',
        redirect: '/workbench',
        meta: { titleKey: 'responsibility.tasks', icon: 'users', inMenu: false, businessOnly: true, permissions: [PERMISSION.SHEET_VIEW, PERMISSION.SHEET_ASSIGN] },
      },
      {
        path: 'responsibilities', name: 'responsibility-rules',
        redirect: '/workbench',
        meta: { titleKey: 'responsibility.title', icon: 'role', inMenu: false, businessOnly: true, permissions: [PERMISSION.RESPONSIBILITY_VIEW] },
      },

      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/UserListView.vue'),
        meta: {
          titleKey: 'nav.userManage',
          icon: 'users',
          inMenu: true,
          permissions: [PERMISSION.ADMIN_USER_MANAGE],
        },
      },

      {
        path: '403',
        name: 'forbidden',
        component: () => import('@/views/error/ForbiddenView.vue'),
        meta: { titleKey: 'page.forbidden.title' },
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: { requiresAuth: false, titleKey: 'page.notFound.title' },
  },
];
